"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 28
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.1,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export default function PremiumPostnatalPricing() {
  return <section id="premiumpostnatalpricing" className="bg-[#FAF7F1] text-[#43362F] pt-12 pb-14 sm:pt-16 sm:pb-20 md:pt-20 md:pb-24 lg:pt-24 lg:pb-28 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 lg:gap-16 items-center">
          {/* Colonne gauche : Contenu éditorial, mentions et actions */}
          <div className="flex flex-col justify-center">
            {/* Petit label haut de casse espacé */}
            <motion.div variants={itemVariants} className="mb-4 sm:mb-5">
              <span className="font-sans text-[0.7rem] sm:text-[0.75rem] font-semibold tracking-[0.24em] uppercase text-[#43362F]/75">ACCOMPAGNEMENT POST-PARTUM À DOMICILE À GENÈVE, NYON</span>
            </motion.div>

            {/* Titre éditorial agrandi en typographie serif */}
            <motion.h1 variants={itemVariants} className="font-serif text-4xl sm:text-5xl lg:text-[3.25rem] xl:text-[3.5rem] font-normal leading-[1.1] tracking-tight mb-6 md:mb-7 text-[#43362F] break-words">Prendre soin des mères et des familles, après la naissance</motion.h1>

            {/* Paragraphe d'introduction */}
            <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg lg:text-[1.125rem] font-light leading-[1.6] text-[#43362F]/85 mb-6 md:mb-7 max-w-xl">Je viens à votre domicile quelques heures par jour, dès votre retour de la maternité sur une durée déterminée ensemble. Je vous offre un soutien logistique, émotionnel et parental.</motion.p>

            {/* Bloc modalités : 3 lignes empilées sans capsule */}
            <motion.div variants={itemVariants} className="font-sans flex flex-col gap-1.5 mb-8 md:mb-9 max-w-xl">
              <div className="text-lg sm:text-xl font-medium text-[#43362F]">
                1, 2 ou 3 mois de soutien
              </div>
              <div className="text-sm sm:text-base text-[#43362F]/80">
                1 ou 2 visites par semaine · Dès 3 h par visite
              </div>
              <div className="text-sm sm:text-base font-medium text-[#43362F]">Forfaits dès 780 CHF · 65 CHF/h (remboursement partiel possible selon ton assurance complémentaire)</div>
            </motion.div>

            {/* Actions : Bouton principal terracotta + lien secondaire empilé dessous */}
            <motion.div variants={itemVariants} className="flex flex-col items-start gap-4 mb-8 md:mb-9">
              <Button asChild className="h-[52px] bg-[#C8735B] hover:bg-[#B36049] text-[#FCFAF7] rounded-lg px-7 text-sm sm:text-base font-sans font-medium tracking-wide inline-flex items-center justify-center gap-3 transition-[background-color,color] duration-300 shadow-sm border-0">
                <Link to="#forfaits-postpartum">
                  <span>DÉCOUVRIR LES FORFAITS</span>
                  <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
                </Link>
              </Button>

              <Link to="/ContactPage" className="text-sm sm:text-base font-sans underline underline-offset-4 text-[#43362F]/80 hover:text-[#43362F] transition-colors py-1 inline-block">
                Parlons de tes besoins
              </Link>
            </motion.div>

            {/* Ligne discrète de localisation */}
            <motion.p variants={itemVariants} className="font-sans text-xs sm:text-sm text-[#43362F]/60 tracking-wide">
              À domicile · Nyon, La Côte &amp; Genève
            </motion.p>
          </div>

          {/* Colonne droite : Illustration au trait avec cadre uniforme arrondi */}
          <motion.div variants={itemVariants} className="relative w-full h-[340px] sm:h-[400px] md:h-[460px] lg:h-[500px] flex items-center justify-center">
            <div className="relative w-full h-full rounded-md overflow-hidden shadow-md bg-[#F8F4ED]">
              <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/mother_newborn_line_art_illustration.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/mother_newborn_line_art_illustration.png-wvc-sizes" alt="Illustration au trait délicate d'une mère avec son nouveau-né dans un moment de tendresse" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" src="https://slow-mama.com/wp-content/uploads/2026/06/a8ab22ce-f8a8-4b14-8cbf-286e5c615fa9-1.png" />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}